
import React, { useMemo, useRef, useState } from 'react';
import { Input, Button, DatePicker, Popover, Avatar, Tag, Tooltip } from 'antd';
import type { InputRef } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { CalendarClock, Check, Search, User, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { richTextToHtml } from '../../components/editor/richText';
import type { Employee } from '../../models/Employee';

interface InlineTaskFormProps {
  statusId: string;
  onCancel: () => void;
  /** `description` is already the HTML that gets stored. */
  onSubmit: (
    statusId: string,
    title: string,
    description: string,
    dueDate?: string | null,
    assigneeId?: number | null,
  ) => void;
  assignableUsers?: Employee[];
  baseCloudinary?: string;
}

const InlineTaskForm: React.FC<InlineTaskFormProps> = ({
  statusId,
  onCancel,
  onSubmit,
  assignableUsers = [],
  baseCloudinary = '',
}) => {
  const { t } = useTranslation();
  const [localTitle, setLocalTitle] = useState<string>('');
  const [localDescription, setLocalDescription] = useState<string>('');
  const [dueDate, setDueDate] = useState<Dayjs | null>(null);
  const [assigneeId, setAssigneeId] = useState<string | undefined>(undefined);
  const [userSearch, setUserSearch] = useState<string>('');

  const [isDateOpen, setIsDateOpen] = useState(false);
  const [isAssigneeOpen, setIsAssigneeOpen] = useState(false);

  const descriptionRef = useRef<InputRef>(null);

  const selectedAssignee = useMemo(
    () => assignableUsers.find((u) => String(u.id) === String(assigneeId)),
    [assignableUsers, assigneeId],
  );

  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return assignableUsers;
    const query = userSearch.toLowerCase();
    return assignableUsers.filter((u) => {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [assignableUsers, userSearch]);

  const handleSubmit = () => {
    if (!localTitle.trim()) return;

    // The quick-add box is plain text; descriptions are stored as HTML, so escape and wrap it here
    // rather than letting a half-typed `<` reach the editor as markup.
    onSubmit(
      statusId,
      localTitle,
      richTextToHtml(localDescription),
      dueDate ? dueDate.toISOString() : null,
      assigneeId ? Number(assigneeId) : null,
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isDateOpen && !isAssigneeOpen) {
      onCancel();
    }
  };

  const assigneeMenuContent = (
    <div className="inline-assignee-menu">
      <Input
        prefix={<Search size={14} style={{ color: '#94a3b8' }} />}
        placeholder={t('Search') || 'Search...'}
        value={userSearch}
        onChange={(e) => setUserSearch(e.target.value)}
        allowClear
        autoFocus
        className="inline-assignee-search"
      />
      <div className="inline-assignee-list">
        {assigneeId && (
          <div
            className="inline-assignee-item inline-assignee-item--unassign"
            onClick={() => {
              setAssigneeId(undefined);
              setIsAssigneeOpen(false);
              setUserSearch('');
            }}
          >
            <div className="inline-assignee-avatar-placeholder">
              <X size={14} />
            </div>
            <span className="inline-assignee-name">{t('NoAssignee') || 'Unassigned'}</span>
          </div>
        )}
        {filteredUsers.length === 0 ? (
          <div className="inline-assignee-empty">{t('NoUsersFound') || 'No users found'}</div>
        ) : (
          filteredUsers.map((user) => {
            const isSelected = String(user.id) === String(assigneeId);
            return (
              <div
                key={user.id}
                className={`inline-assignee-item ${isSelected ? 'is-selected' : ''}`}
                onClick={() => {
                  setAssigneeId(String(user.id));
                  setIsAssigneeOpen(false);
                  setUserSearch('');
                }}
              >
                <Avatar
                  size={22}
                  src={
                    user.photoId
                      ? `${baseCloudinary}/image/upload/w_100/${user.photoId}`
                      : undefined
                  }
                  className="inline-assignee-avatar"
                >
                  {user.firstName?.[0]}
                </Avatar>
                <span className="inline-assignee-name">
                  {user.firstName} {user.lastName}
                </span>
                {isSelected && <Check size={14} className="inline-assignee-check" />}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <div className="inline-task-form" onKeyDown={handleKeyDown}>
      <Input
        placeholder={t('TaskTitle')}
        value={localTitle}
        onChange={(e) => setLocalTitle(e.target.value)}
        onPressEnter={(e) => {
          if (!e.shiftKey && localTitle.trim()) {
            e.preventDefault();
            handleSubmit();
          }
        }}
        autoFocus
        className="inline-task-title hover-effect"
      />
      <Input.TextArea
        ref={descriptionRef}
        placeholder={t('Description')}
        value={localDescription}
        onChange={(e) => setLocalDescription(e.target.value)}
        autoSize={{ minRows: 2, maxRows: 4 }}
        className="inline-task-description hover-effect"
      />

      <div className="inline-task-footer">
        <div className="inline-task-tools">
          {/* Due Date Picker Trigger */}
          <div className="inline-tool-wrapper">
            {dueDate ? (
              <Tag
                closable
                closeIcon={<X size={12} />}
                onClose={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDueDate(null);
                }}
                className="inline-task-chip"
                icon={<CalendarClock size={13} style={{ marginRight: 4 }} />}
                onClick={() => setIsDateOpen(true)}
              >
                <span>{dueDate.format('D MMM')}</span>
              </Tag>
            ) : (
              <Tooltip title={t('DueDate')}>
                <Button
                  type="text"
                  size="small"
                  className="inline-tool-btn hover-effect"
                  icon={<CalendarClock size={15} />}
                  onClick={() => setIsDateOpen(true)}
                />
              </Tooltip>
            )}
            <DatePicker
              open={isDateOpen}
              onOpenChange={setIsDateOpen}
              value={dueDate}
              onChange={(date) => {
                setDueDate(date);
                setIsDateOpen(false);
              }}
              style={{
                position: 'absolute',
                opacity: 0,
                pointerEvents: 'none',
                width: 0,
                height: 0,
                padding: 0,
                border: 0,
              }}
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
            />
          </div>

          {/* Assignee Selector Trigger */}
          {assignableUsers.length > 0 && (
            <div className="inline-tool-wrapper">
              <Popover
                open={isAssigneeOpen}
                onOpenChange={setIsAssigneeOpen}
                trigger="click"
                placement="bottomLeft"
                content={assigneeMenuContent}
              >
                {selectedAssignee ? (
                  <Tag
                    closable
                    closeIcon={<X size={12} />}
                    onClose={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAssigneeId(undefined);
                      setIsAssigneeOpen(false);
                      setUserSearch('');
                    }}
                    className="inline-task-chip inline-task-chip-user"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsAssigneeOpen((prev) => !prev);
                    }}
                  >
                    <Avatar
                      size={16}
                      src={
                        selectedAssignee.photoId
                          ? `${baseCloudinary}/image/upload/w_100/${selectedAssignee.photoId}`
                          : undefined
                      }
                      style={{ marginRight: 4, fontSize: 10 }}
                    >
                      {selectedAssignee.firstName?.[0]}
                    </Avatar>
                    <span>{selectedAssignee.firstName}</span>
                  </Tag>
                ) : (
                  <Tooltip title={t('SelectAssignee')}>
                    <Button
                      type="text"
                      size="small"
                      className="inline-tool-btn hover-effect"
                      icon={<User size={15} />}
                    />
                  </Tooltip>
                )}
              </Popover>
            </div>
          )}
        </div>

        <div className="inline-task-actions">
          <Button
            type="primary"
            onClick={handleSubmit}
            disabled={!localTitle.trim()}
            className="hover-effect"
          >
            {t('Add')}
          </Button>
          <Button onClick={onCancel} className="hover-effect">
            {t('Cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InlineTaskForm;